import { Form, Modal, Select, message } from 'antd';
import React, { FC, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { supplierUpdateFieldTechLocations } from '../../thunks/supplier_contacts_thunks';
import { stockLocationsRestCrudThunksForSupplier } from '../../thunks/stock_locations_thunks';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import SelectWithAllToggle from '../common/SelectWithAllToggle';

const TARGET_COLLECTION = 'fieldTechAccessibleStockLocationsDropdown';

const EditAccessibleStockLocationsModal: FC<any> = ({
	onCancel,
	onSuccess,
	email,
	hasAccessToAllStockLocations: initialHasAccessToAllStockLocations,
	stockLocationIds: initialStockLocationIds,
	stockLocations,
	fetchStockLocations,
	fetchMultipleStockLocations,
	updateFieldTechLocations,
}): React.ReactElement => {
	const [form] = Form.useForm();
	const [updating, setUpdating] = useState(false);

	const handleSubmit = useCallback(
		(values) => {
			const hasAccessToAllStockLocations = !!values.hasAccessToAllStockLocations;
			setUpdating(true);
			updateFieldTechLocations({
				userId: email,
				hasAccessToAllStockLocations,
				// Only send stockLocationIds when granting specific access. Including it (even as an
				// empty list) while "all" is on makes the backend treat ids as explicitly supplied and
				// force hasAccessToAllStockLocations back off — wiping access instead of granting all.
				...(hasAccessToAllStockLocations
					? {}
					: { stockLocationIds: nullSafeGetOrElse('stockLocationIds', values, []) }),
			})
				.then(() => onSuccess && onSuccess())
				.catch(() => message.error('Unable to update accessible stock locations.'))
				.finally(() => setUpdating(false));
		},
		[email, onSuccess, updateFieldTechLocations]
	);

	return (
		<Modal
			visible={true}
			width={600}
			title={'Edit Accessible Stock Locations'}
			okText={'Save'}
			onCancel={onCancel}
			onOk={form.submit}
			closable={true}
			confirmLoading={updating}
			destroyOnClose={true}
		>
			<Form
				form={form}
				layout="vertical"
				className="supplierForm"
				preserve={false}
				initialValues={{
					hasAccessToAllStockLocations: !!initialHasAccessToAllStockLocations,
					stockLocationIds: initialStockLocationIds || [],
				}}
				onFinish={handleSubmit}
			>
				<SelectWithAllToggle
					label={'Stock Locations'}
					name={'stockLocationIds'}
					allName={'hasAccessToAllStockLocations'}
					form={form}
					selectAllPlaceholder={'All stock locations'}
				>
					<OWAsyncSelect
						mode="multiple"
						stateSlice={stockLocations}
						targetCollectionName={TARGET_COLLECTION}
						fetchMultiple={(ids, targetCollectionName) => {
							fetchMultipleStockLocations(ids, targetCollectionName);
						}}
						fetchData={(
							searchText,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) => {
							fetchStockLocations(
								{ name: searchText },
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							);
						}}
						renderRecord={(stockLocation) => (
							<Select.Option value={stockLocation.id} key={stockLocation.id}>
								{stockLocation.name}
							</Select.Option>
						)}
						sortBy={{ sort_by: 'name', order: 'ascend' }}
					/>
				</SelectWithAllToggle>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	stockLocations: state.stock_locations,
});

const mapDispatchToProps = (dispatch) => ({
	updateFieldTechLocations: (payload) => dispatch(supplierUpdateFieldTechLocations(payload)),
	fetchMultipleStockLocations: (ids, targetCollectionName) =>
		dispatch(stockLocationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchStockLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			stockLocationsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditAccessibleStockLocationsModal);
